// AiChatFloatingV2 — V2 floating chat dock.
//
// Mirrors the existing `AiChatFloating` (same prop surface from
// `AiChatFloatingProps`) but renders with the v2 chrome:
//   - When open: a 380×480 panel pinned to the bottom-right with a sparkle
//     header + close button. The body hosts the existing AiChat (so all
//     business logic, sessions, streaming is reused unchanged).
//   - When closed: a 52×52 amber sparkle button (the FAB).
//
// Self-contained — wraps the existing AiChat which provisions its own
// AiChatProvider, so floating chats don't share state with any other chat
// surface on the page.

import React, { forwardRef, useCallback, useState } from 'react';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Slide from '@mui/material/Slide';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { CloseIcon, SparkleIcon } from './icons';
import AiChat from '../AiChat/AiChat';
import type { AiChatFloatingProps, AiChatHandle } from '../AiChat/AiChatTypes';
import { RunProgressStripV2 } from './RunProgressStripV2';

// 440px (was 380) gives PromptV2 enough width that its pill chrome
// (watermark + sparkle/history tools on the left, send button on the
// right, autosizing textarea in the middle) sits cleanly inside the
// panel. At 380 the watermark "Arraste qualquer imagem para cá OU
// copie e cole" hugs the pill's right rounded edge and the whole row
// reads as cramped/leaking.
const DEFAULT_WIDTH = 440;
const DEFAULT_HEIGHT = 480;

export const AiChatFloatingV2 = forwardRef<AiChatHandle, AiChatFloatingProps>((props, ref) => {
    const {
        position,
        chatPositionOffSet,
        fabContent,
        width = DEFAULT_WIDTH,
        height = DEFAULT_HEIGHT,
        title = 'Assistente',
        onVisibleQuestionChange: externalOnVisibleQuestionChange,
        onFloatingOpened,
        ...aiChatProps
    } = props;

    const anchor = { bottom: 20, right: 20, ...position };
    const [open, setOpen] = useState(false);
    const [visibleQuestion, setVisibleQuestion] = useState<string | null>(null);

    const handleVisibleQuestionChange = useCallback(
        (question: string | null) => {
            let mapped = question;
            if (props.onRenderPrompt && question) {
                const { promptAsString } = props.onRenderPrompt(question);
                mapped = promptAsString ?? null;
            }
            setVisibleQuestion(mapped ?? '');
            externalOnVisibleQuestionChange?.(question);
        },
        [externalOnVisibleQuestionChange, props.onRenderPrompt],
    );

    const headerLabel = visibleQuestion?.trim() || title;

    const panelBottom =
        anchor.bottom != null ? anchor.bottom + (chatPositionOffSet?.bottom ?? 0) : undefined;
    const panelRight =
        anchor.right != null ? anchor.right + (chatPositionOffSet?.right ?? 0) : undefined;
    const panelLeft =
        anchor.left != null ? anchor.left + (chatPositionOffSet?.left ?? 0) : undefined;
    const panelTop =
        anchor.top != null ? anchor.top + (chatPositionOffSet?.top ?? 0) : undefined;

    const handleOpen = () => {
        setOpen(true);
        onFloatingOpened?.();
    };

    return (
        <>
            {/* `mountOnEnter` was removed deliberately. With it, the
                inner <AiChat> only mounts the first time the FAB is
                clicked — but `onFloatingOpened` fires *synchronously*
                on that click, before <AiChat>'s forwardRef has wired
                up the host's `chatHandleRef`. Hosts that use the open
                callback to send a context message (e.g. the dashboard
                view sending the dashboard JSON) called `sendMessage`
                on a null handle and the message was silently lost.
                Mounting eagerly matches v1's `AiChatFloating` contract
                and makes the handle available before the click; the
                cost is one WS + session per dashboard view, but the
                parent already gates this component on having a loaded
                dashboard so the cost is bounded. */}
            <Slide direction="up" in={open} unmountOnExit={false}>
                <Paper
                    elevation={0}
                    sx={(theme) => ({
                        position: 'fixed',
                        bottom: panelTop != null ? undefined : panelBottom,
                        top: panelTop,
                        right: panelRight,
                        left: panelLeft,
                        width,
                        height,
                        backgroundColor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: `${theme.shape.radiusLg}px`,
                        boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        zIndex: 10000,
                        visibility: open ? 'visible' : 'hidden',
                        pointerEvents: open ? 'auto' : 'none',
                    })}
                >
                    {/* Header */}
                    <Box
                        sx={(theme) => ({
                            px: 1.75,
                            py: 1.25,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                            flexShrink: 0,
                        })}
                    >
                        <Box sx={(theme) => ({ color: theme.palette.primary.main, display: 'inline-flex' })}>
                            <SparkleIcon />
                        </Box>
                        <Tooltip title={headerLabel} enterDelay={500}>
                            <Typography
                                sx={(theme) => ({
                                    flex: 1,
                                    fontSize: 13,
                                    fontWeight: 500,
                                    color: theme.palette.text.primary,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                })}
                            >
                                {headerLabel}
                            </Typography>
                        </Tooltip>
                        <IconButton
                            size="small"
                            onClick={() => setOpen(false)}
                            sx={(theme) => ({ color: theme.palette.text.secondary, p: 0.5 })}
                            aria-label="Fechar"
                        >
                            <CloseIcon size={14} />
                        </IconButton>
                    </Box>

                    {/* Body */}
                    <Box sx={(theme) => ({ flex: 1, minHeight: 0, overflow: 'hidden', backgroundColor: theme.palette.background.paper })}>
                        <AiChat
                            ref={ref}
                            {...aiChatProps}
                            borderRadius={0}
                            elevation={0}
                            onVisibleQuestionChange={handleVisibleQuestionChange}
                            // Slim activity strip below the prompt — replaces
                            // the full RunProgressPanelV2 sidebar (which won't
                            // fit a 440-wide floating panel) with a one-row
                            // status line that auto-hides when there's no
                            // turn / tasks / connection issue worth showing.
                            bottomSlot={<RunProgressStripV2 />}
                        />
                    </Box>
                </Paper>
            </Slide>

            {!open && (
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: anchor.bottom,
                        top: anchor.top,
                        right: anchor.right,
                        left: anchor.left,
                        zIndex: 10000,
                    }}
                >
                    {fabContent ? (
                        <Box onClick={handleOpen} sx={{ cursor: 'pointer' }}>
                            {fabContent}
                        </Box>
                    ) : (
                        <Box
                            role="button"
                            tabIndex={0}
                            onClick={handleOpen}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleOpen(); }}
                            aria-label="Abrir assistente"
                            sx={(theme) => ({
                                width: 52,
                                height: 52,
                                borderRadius: '50%',
                                backgroundColor: theme.palette.primary.main,
                                color: '#1a1208',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
                                transition: 'transform .15s, box-shadow .15s',
                                '&:hover': {
                                    transform: 'scale(1.06)',
                                    boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
                                },
                            })}
                        >
                            <SparkleIcon size={20} />
                        </Box>
                    )}
                </Box>
            )}
        </>
    );
});

AiChatFloatingV2.displayName = 'AiChatFloatingV2';
